import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useEscapeLayer from "../useEscape";
import { apiBlob } from "../../api";
import "./rooms.css";

/*
 * The room's shared pieces: the dialog frame every add and edit window sits in
 * (RecapPreview, EmailTrim, LibraryPicker and EditDialogs boards share it), the
 * on/off switch, the drag handle and the thumbnails.
 */

/** A dialog over everything: portalled, Esc and the backdrop close it, focus goes in. */
export function Dialog({ title, eyebrow = null, sub = null, size = "", onClose, children, foot }) {
  const ref = useRef(null);
  useEscapeLayer(true, onClose);
  useEffect(() => {
    const first = ref.current && ref.current.querySelector("input, textarea, select, button:not(.rm-x)");
    if (first) first.focus();
  }, []);
  const id = useRef(`rm-d-${Math.random().toString(36).slice(2)}`).current;
  const tree = (
    <div className="rm rm-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`rm-dialog${size ? ` rm-dialog--${size}` : ""}`} role="dialog" aria-modal="true" aria-labelledby={id}>
        <div className="rm-dialog__head">
          <div>
            {eyebrow && <span className="rm-label">{eyebrow}</span>}
            <h2 id={id}>{title}</h2>
            {sub && <div className="rm-sub">{sub}</div>}
          </div>
          <button type="button" className="rm-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="rm-dialog__body">{children}</div>
        {foot && <div className="rm-dialog__foot">{foot}</div>}
      </div>
    </div>
  );
  return typeof document === "undefined" ? tree : createPortal(tree, document.body);
}

export function Switch({ on, label, onChange, disabled = false }) {
  return (
    <button type="button" role="switch" aria-checked={on ? "true" : "false"} aria-label={label} className="rm-switch" disabled={disabled} onClick={() => onChange(!on)}>
      <span className="rm-switch__track"><span className="rm-switch__knob" /></span>
      {on ? "On" : "Off"}
    </button>
  );
}

export function DragDots() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">
      <g fill="#8F8C83">
        <circle cx="2.5" cy="3" r="1.5" />
        <circle cx="7.5" cy="3" r="1.5" />
        <circle cx="2.5" cy="8" r="1.5" />
        <circle cx="7.5" cy="8" r="1.5" />
        <circle cx="2.5" cy="13" r="1.5" />
        <circle cx="7.5" cy="13" r="1.5" />
      </g>
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#77746B" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </svg>
  );
}

export function Check() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

const THUMB_TEXT = { deck: "PDF", case_study: "CASE", example_ads: "ADS", link: "LINK", demo: "DEMO" };

/** The library's small picture: the first image when there is one, else the kind's colour. */
export function Thumb({ item, src = null }) {
  return (
    <span aria-hidden="true" className={`rm-thumb rm-thumb--${item.kind}`}>
      {src ? <img src={src} alt="" /> : item.url && item.link ? (item.link.video ? "▶" : THUMB_TEXT.link) : THUMB_TEXT[item.kind] || ""}
    </span>
  );
}

export function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Run an async write with busy/error state; returns a stable runner. */
export function useRunner(setBusy, setErr) {
  return async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      return await fn();
    } catch (e) {
      setErr(e.message || "Something went wrong");
      return undefined;
    } finally {
      setBusy(false);
    }
  };
}

/** An auth-gated stored file as an object URL (thumbnails in the CRM). */
export function useFileUrl(fileId) {
  const [url, setUrl] = React.useState(null);
  useEffect(() => {
    if (!fileId) return undefined;
    let live = true;
    let objectUrl = null;
    apiBlob(`/room-library/files/${fileId}`)
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);
  return url;
}

/** The library item's thumbnail, with its first image when it has one. */
export function LibThumb({ item }) {
  const img = (item.files || []).find((f) => /^image\//.test(f.content_type));
  const src = useFileUrl(item.cover_file_id || (img ? img.id : null));
  return <Thumb item={item} src={src} />;
}
